import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service to manage global loading state for route transitions and lazy loading
 */
@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private activeRequests = 0;
  private overlayHostClaimed = false;

  /**
   * Observable to subscribe for loading state changes
   */
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  /**
   * Show the loader by incrementing the active request count
   */
  public show(): void {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.loadingSubject.next(true);
    }
  }

  /**
   * Hide the loader by decrementing the active request count
   * Loader is hidden only when all active requests are complete
   */
  public hide(): void {
    this.activeRequests--;
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.loadingSubject.next(false);
    }
  }

  /**
   * Force hide the loader and reset the counter
   * Useful for error recovery scenarios
   */
  public reset(): void {
    this.activeRequests = 0;
    this.loadingSubject.next(false);
  }

  /**
   * Get the current loading state
   * @returns Current loading state
   */
  public isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Get the count of active requests
   * Useful for debugging
   * @returns Number of active requests
   */
  public getActiveRequestCount(): number {
    return this.activeRequests;
  }

  /**
   * Lazy-loaded sub-app shells render their own <app-global-loader>, so only the
   * first one to claim the host is allowed to paint the overlay.
   * @returns true when the caller owns the overlay
   */
  public claimOverlayHost(): boolean {
    if (this.overlayHostClaimed) {
      return false;
    }
    this.overlayHostClaimed = true;
    return true;
  }

  public releaseOverlayHost(): void {
    this.overlayHostClaimed = false;
  }
}
